/**
 * Authenticated "Termos assinados" (`/termos-assinados`), reached from the
 * profile. Lists every acceptance term the account signed — its own and its
 * dependents', in every center — including legacy per-contract terms and
 * centers that later turned the term off. Each row opens the stored receipt
 * on the public page (`/terms/:token?a=<id>`).
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { Body, Caption, H4 } from '@meiuca/ui-components'
import { ArrowLeft, ChevronRight, FileSignature, Loader2 } from 'lucide-react'
import { formatDateForUI, parseAPIDate } from '@meiuca/web-foundation'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { LoginRequiredEmptyState } from '@/components/LoginRequiredEmptyState'
import { useAuth } from '@/contexts/AuthContext'
import {
  trainingCenterPlanService,
  type TrainingCenterTermsItem
} from '@/services/trainingCenterPlanService'

export function SignedTermsPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const [items, setItems] = useState<TrainingCenterTermsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.SIGNED_TERMS)
  }, [])

  const load = () => {
    setLoading(true)
    setError(false)
    trainingCenterPlanService
      .listTerms()
      .then((result) => setItems(result.filter((item) => item.terms.history.length > 0)))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (isAuthenticated) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <button
          onClick={() => navigate('/perfil')}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Perfil
        </button>

        <div className="mb-6">
          <H4 className="text-text-primary font-semibold mb-2">Termos assinados</H4>
          <Body className="text-text-secondary">
            Os termos de aceite que você assinou com os centros de treinamento.
          </Body>
        </div>

        {isAuthLoading ? (
          <Spinner />
        ) : !isAuthenticated ? (
          <LoginRequiredEmptyState
            title="Entre para ver seus termos"
            description="Faça login para ver os termos que você assinou."
          />
        ) : loading ? (
          <Spinner />
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Body className="text-feedback-error mb-4">Erro ao carregar seus termos.</Body>
            <button
              onClick={load}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 mb-4 rounded-full bg-background-secondary flex items-center justify-center">
              <FileSignature className="w-8 h-8 text-text-tertiary" />
            </div>
            <H4 className="text-text-primary mb-2">Nenhum termo assinado</H4>
            <Body className="text-text-secondary text-center max-w-md">
              Quando você assinar o termo de um centro, ele aparece aqui.
            </Body>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={`${item.trainingCenter.id}-${item.studentCustomer.id}`}
                className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light"
              >
                <Body className="text-sm font-semibold text-text-primary m-0">{item.trainingCenter.name}</Body>
                {item.isDependent && (
                  <Caption className="text-text-secondary">Aluno: {item.studentCustomer.name}</Caption>
                )}
                <div className="mt-2 divide-y divide-border-light">
                  {item.terms.history.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => {
                        analyticsService.track(ANALYTICS_EVENTS.CONTRACT_TERMS_RECEIPT_OPENED, { kind: entry.kind })
                        navigate(`/terms/${entry.receiptToken}?a=${entry.id}`)
                      }}
                      className="w-full flex items-center justify-between gap-3 py-3 text-left"
                    >
                      <span className="min-w-0">
                        <Body className="text-sm font-medium text-text-primary m-0">
                          {entry.kind === 'general'
                            ? `Termo do centro · versão ${entry.templateVersion}`
                            : 'Termo do contrato (modelo anterior)'}
                          {entry.isCurrent && (
                            <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-feedback-success/20 text-feedback-success">
                              Atual
                            </span>
                          )}
                        </Body>
                        <Caption className="text-text-secondary">
                          {formatDateForUI(parseAPIDate(entry.acceptedAt), 'medium')} · {entry.signerDisplayName}
                        </Caption>
                      </span>
                      <ChevronRight className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function Spinner() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
    </div>
  )
}
